import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { serverTypeLabel } from '@/utils/server-type';
import {
  ArrowLeft,
  Shield,
  Wifi,
  Server,
  Trophy,
  Calendar,
  Tv,
  ChevronDown,
  ChevronRight,
  Radio,
  Circle,
  CheckCircle2,
  AlertCircle,
  Key,
  Layers,
  User,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usersService, getErrorMessage } from '@/services';
import type { AdminUserDetail as AdminUserDetailData } from '@/services';
import dayjs from 'dayjs';
import { clsx } from 'clsx';

type DetailTab = 'overview' | 'dazn' | 'proxies' | 'xaccel' | 'sports' | 'schedule' | 'linear';

const STATUS_LABELS: Record<number, { key: string; className: string }> = {
  0: { key: 'schedule.statusScheduled', className: 'bg-blue-50 text-blue-600' },
  1: { key: 'schedule.statusLive', className: 'bg-emerald-50 text-emerald-600' },
  2: { key: 'schedule.statusEnded', className: 'bg-gray-100 text-gray-500' },
  3: { key: 'schedule.statusError', className: 'bg-red-50 text-red-600' },
};

const CHANNEL_MODE_LABELS: Record<string, string> = {
  numbered: 'schedule.channelModeNumbered',
  team: 'schedule.channelModeTeam',
  both: 'schedule.channelModeBoth',
};

export default function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [data, setData] = useState<AdminUserDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSports, setExpandedSports] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!id) return;
    const fetchDetail = async () => {
      try {
        const detail = await usersService.getUserDetail(parseInt(id));
        setData(detail);
        // Expand all sports by default
        setExpandedSports(new Set(detail.sports.map(s => s.id)));
      } catch (error) {
        toast.error(getErrorMessage(error) || t('common.error'));
        navigate('/admin/users');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetail();
  }, [id, navigate, t]);

  const toggleSport = (sportId: number) => {
    setExpandedSports(prev => {
      const next = new Set(prev);
      if (next.has(sportId)) next.delete(sportId);
      else next.add(sportId);
      return next;
    });
  };

  const tabs: { key: DetailTab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: t('admin.overview'), icon: <Shield size={16} /> },
    { key: 'dazn', label: 'DAZN Account', icon: <User size={16} /> },
    { key: 'proxies', label: t('nav.proxies'), icon: <Wifi size={16} /> },
    { key: 'xaccel', label: serverTypeLabel('xaccel'), icon: <Server size={16} /> },
    { key: 'sports', label: t('nav.sports'), icon: <Trophy size={16} /> },
    { key: 'schedule', label: t('nav.schedule'), icon: <Calendar size={16} /> },
    { key: 'linear', label: t('admin.linearTab'), icon: <Tv size={16} /> },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 bg-dark-700 rounded animate-pulse" />
          <div className="h-6 w-48 bg-dark-700 rounded animate-pulse" />
        </div>
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { user, daznAccount, counts } = data;

  const tokenExpired = daznAccount?.tokenStatus?.expiresAt
    ? new Date(daznAccount.tokenStatus.expiresAt) < new Date()
    : false;

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: t('nav.proxies'), value: counts.proxies, icon: <Wifi size={18} className="text-blue-600" />, bg: 'bg-blue-50' },
          { label: serverTypeLabel('xaccel'), value: counts.xaccelServers, icon: <Server size={18} className="text-purple-600" />, bg: 'bg-purple-50' },
          { label: t('nav.sports'), value: counts.sports, icon: <Trophy size={18} className="text-amber-600" />, bg: 'bg-amber-50' },
          { label: t('nav.competitions'), value: counts.competitions, icon: <Layers size={18} className="text-indigo-600" />, bg: 'bg-indigo-50' },
          { label: t('admin.liveCount'), value: counts.liveNow, icon: <Radio size={18} className="text-emerald-600" />, bg: 'bg-emerald-50' },
          { label: t('admin.scheduledCount'), value: counts.scheduledToday, icon: <Circle size={18} className="text-sky-600" />, bg: 'bg-sky-50' },
          { label: t('admin.linearTab'), value: counts.linearChannels, icon: <Tv size={18} className="text-rose-600" />, bg: 'bg-rose-50' },
          { label: t('admin.resolutionsLabel'), value: counts.resolutions, icon: <Layers size={18} className="text-teal-600" />, bg: 'bg-teal-50' },
        ].map((stat, i) => (
          <div key={i} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-400 text-xs">{stat.label}</p>
                <p className="text-2xl font-bold text-dark-100 mt-0.5">{stat.value}</p>
              </div>
              <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center`}>
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Two cards side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* DAZN Account */}
        <div className="card">
          <h3 className="text-base font-semibold text-dark-100 mb-4">{t('admin.daznAccountInfo')}</h3>
          {daznAccount ? (
            <div className="space-y-3">
              <InfoRow label="Email" value={daznAccount.dazn_email} />
              <InfoRow label={t('dazn.password')} value={
                <span className="font-mono text-xs bg-dark-700/50 px-2 py-0.5 rounded">
                  {daznAccount.dazn_password || '-'}
                </span>
              } />
              <InfoRow label={t('dazn.deviceId')} value={
                <span className="font-mono text-xs bg-dark-700/50 px-2 py-0.5 rounded">
                  {daznAccount.dazn_device_id || '-'}
                </span>
              } />
              <InfoRow label={t('dazn.pin')} value={daznAccount.dazn_pin || '-'} />
              <InfoRow label={t('admin.country')} value={daznAccount.dazn_country} />
              <InfoRow label={t('settings.language')} value={daznAccount.dazn_language} />
              <InfoRow label={t('admin.timezone')} value={daznAccount.dazn_timezone} />
              <InfoRow label={t('admin.subscription')} value={daznAccount.subscription_status || '-'} />
              <InfoRow label={t('dazn.tokenStatus')} value={
                <span className={clsx(
                  'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium',
                  !daznAccount.tokenStatus.hasToken
                    ? 'bg-gray-100 text-gray-500'
                    : tokenExpired
                      ? 'bg-red-50 text-red-600'
                      : 'bg-emerald-50 text-emerald-600'
                )}>
                  <Key size={12} />
                  {!daznAccount.tokenStatus.hasToken
                    ? t('admin.tokenMissing')
                    : tokenExpired
                      ? t('admin.tokenExpired')
                      : t('admin.tokenValid')}
                </span>
              } />
              {daznAccount.dazn_first_name && (
                <InfoRow label={t('common.name')} value={`${daznAccount.dazn_first_name} ${daznAccount.dazn_last_name || ''}`} />
              )}
            </div>
          ) : (
            <p className="text-dark-400 text-sm">{t('admin.noDaznAccount')}</p>
          )}
        </div>

        {/* User Info */}
        <div className="card">
          <h3 className="text-base font-semibold text-dark-100 mb-4">{t('admin.userInfo')}</h3>
          <div className="space-y-3">
            <InfoRow label={t('common.createdAt')} value={dayjs(user.created_at).format('DD/MM/YYYY HH:mm')} />
            <InfoRow label={t('admin.lastAccess')} value={
              user.last_login_at ? dayjs(user.last_login_at).format('DD/MM/YYYY HH:mm') : t('admin.neverConnected')
            } />
            <InfoRow label={t('admin.expiresAt')} value={
              user.expires_at ? dayjs(user.expires_at).format('DD/MM/YYYY') : t('admin.neverExpires')
            } />
            <InfoRow label="Setup" value={
              <span className={clsx(
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                user.setup_completed ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
              )}>
                {user.setup_completed ? t('admin.setupComplete') : t('admin.setupIncomplete')}
              </span>
            } />
            <InfoRow label={t('settings.language')} value={user.language?.toUpperCase() || '-'} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderProxies = () => (
    <div className="card">
      {data.proxies.length === 0 ? (
        <p className="text-dark-400 text-sm">{t('admin.noProxies')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('common.name')}</th>
                <th className="text-left py-2 pr-4 text-dark-400 font-medium">Host:Port</th>
                <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('proxy.type')}</th>
                <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('proxy.username')}</th>
                <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('proxy.password')}</th>
                <th className="text-center py-2 pr-4 text-dark-400 font-medium">{t('proxy.default')}</th>
                <th className="text-left py-2 text-dark-400 font-medium">{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {data.proxies.map((proxy) => (
                <tr key={proxy.id} className="border-b border-dark-700 last:border-0">
                  <td className="py-2.5 pr-4 font-medium text-dark-100">{proxy.name}</td>
                  <td className="py-2.5 pr-4 text-dark-300 font-mono text-xs">{proxy.proxy_host}:{proxy.proxy_port}</td>
                  <td className="py-2.5 pr-4 text-dark-300">{proxy.proxy_type}</td>
                  <td className="py-2.5 pr-4 text-dark-300 font-mono text-xs">{proxy.proxy_username || '-'}</td>
                  <td className="py-2.5 pr-4 text-dark-300 font-mono text-xs">{proxy.proxy_password || '-'}</td>
                  <td className="py-2.5 pr-4 text-center">
                    {proxy.is_default ? (
                      <CheckCircle2 size={16} className="text-emerald-500 inline" />
                    ) : (
                      <span className="text-dark-500">-</span>
                    )}
                  </td>
                  <td className="py-2.5">
                    <span className={clsx(
                      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                      proxy.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'
                    )}>
                      {proxy.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderXaccel = () => (
    <div className="card">
      {data.xaccelServers.length === 0 ? (
        <p className="text-dark-400 text-sm">{t('admin.noXaccel')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('common.name')}</th>
                <th className="text-left py-2 pr-4 text-dark-400 font-medium">URL</th>
                <th className="text-left py-2 pr-4 text-dark-400 font-medium">Token</th>
                <th className="text-center py-2 pr-4 text-dark-400 font-medium">{t('proxy.default')}</th>
                <th className="text-left py-2 text-dark-400 font-medium">{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {data.xaccelServers.map((server) => (
                <tr key={server.id} className="border-b border-dark-700 last:border-0">
                  <td className="py-2.5 pr-4 font-medium text-dark-100">{server.name}</td>
                  <td className="py-2.5 pr-4 text-dark-300 font-mono text-xs">{server.server_url}</td>
                  <td className="py-2.5 pr-4 text-dark-300 font-mono text-xs max-w-[200px] truncate" title={server.server_token}>
                    {server.server_token}
                  </td>
                  <td className="py-2.5 pr-4 text-center">
                    {server.is_default ? (
                      <CheckCircle2 size={16} className="text-emerald-500 inline" />
                    ) : (
                      <span className="text-dark-500">-</span>
                    )}
                  </td>
                  <td className="py-2.5">
                    <span className={clsx(
                      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                      server.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'
                    )}>
                      {server.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSports = () => (
    <div className="space-y-3">
      {data.sports.length === 0 ? (
        <div className="card">
          <p className="text-dark-400 text-sm">{t('admin.noSports')}</p>
        </div>
      ) : (
        data.sports.map((sport) => (
          <div key={sport.id} className="card">
            <button
              onClick={() => toggleSport(sport.id)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                {expandedSports.has(sport.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span className="font-semibold text-dark-100">{sport.name}</span>
                <span className="text-xs text-dark-400">
                  ({sport.competitions.length} {t('nav.competitions').toLowerCase()})
                </span>
              </div>
            </button>
            {expandedSports.has(sport.id) && sport.competitions.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-dark-700">
                      <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('common.name')}</th>
                      <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('competitions.channelNaming')}</th>
                      <th className="text-center py-2 pr-4 text-dark-400 font-medium">{t('competitions.numChannels')}</th>
                      <th className="text-left py-2 pr-4 text-dark-400 font-medium">{serverTypeLabel('xaccel')}</th>
                      <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('competitions.resolutions')}</th>
                      <th className="text-center py-2 text-dark-400 font-medium">{t('common.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sport.competitions.map((comp) => (
                      <tr key={comp.id} className="border-b border-dark-700 last:border-0">
                        <td className="py-2.5 pr-4 font-medium text-dark-100">{comp.name}</td>
                        <td className="py-2.5 pr-4 text-dark-300">
                          {comp.channel_mode ? t(CHANNEL_MODE_LABELS[comp.channel_mode] || comp.channel_mode) : '-'}
                        </td>
                        <td className="py-2.5 pr-4 text-center text-dark-300">{comp.num_channels || '-'}</td>
                        <td className="py-2.5 pr-4 text-dark-300">{comp.xaccel_server_name || '-'}</td>
                        <td className="py-2.5 pr-4 text-dark-300 text-xs">{comp.resolutions || '-'}</td>
                        <td className="py-2.5 text-center">
                          <span className={clsx(
                            'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                            comp.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'
                          )}>
                            {comp.is_active ? t('common.active') : t('common.inactive')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {expandedSports.has(sport.id) && sport.competitions.length === 0 && (
              <p className="mt-3 text-dark-400 text-sm">{t('common.noData')}</p>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderSchedule = () => (
    <div className="space-y-4">
      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: t('admin.scheduledCount'), value: data.schedule.stats.scheduled, icon: <Circle size={14} className="text-blue-500" />, bg: 'bg-blue-50', text: 'text-blue-700' },
          { label: t('admin.liveCount'), value: data.schedule.stats.live, icon: <Radio size={14} className="text-emerald-500" />, bg: 'bg-emerald-50', text: 'text-emerald-700' },
          { label: t('admin.completedCount'), value: data.schedule.stats.completed, icon: <CheckCircle2 size={14} className="text-gray-400" />, bg: 'bg-gray-50', text: 'text-gray-700' },
          { label: t('admin.errorCount'), value: data.schedule.stats.errors, icon: <AlertCircle size={14} className="text-red-500" />, bg: 'bg-red-50', text: 'text-red-700' },
        ].map((stat, i) => (
          <div key={i} className={`${stat.bg} rounded-lg px-4 py-3`}>
            <div className="flex items-center gap-2">
              {stat.icon}
              <span className={`text-sm font-medium ${stat.text}`}>{stat.label}</span>
            </div>
            <p className={`text-2xl font-bold ${stat.text} mt-1`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Schedule table */}
      <div className="card">
        {data.schedule.today.length === 0 ? (
          <p className="text-dark-400 text-sm">{t('admin.noSchedule')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('schedule.startTime')}</th>
                  <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('schedule.event')}</th>
                  <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('schedule.competition')}</th>
                  <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('schedule.channel')}</th>
                  <th className="text-center py-2 text-dark-400 font-medium">{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {data.schedule.today.map((event) => {
                  const statusDef = STATUS_LABELS[event.status] || STATUS_LABELS[0];
                  return (
                    <tr key={event.id} className="border-b border-dark-700 last:border-0">
                      <td className="py-2.5 pr-4 text-dark-300 font-mono text-xs">
                        {dayjs(event.event_start_time).format('HH:mm')}
                      </td>
                      <td className="py-2.5 pr-4 font-medium text-dark-100">{event.event_title}</td>
                      <td className="py-2.5 pr-4 text-dark-300">{event.competition_name || '-'}</td>
                      <td className="py-2.5 pr-4 text-dark-300 font-mono text-xs">{event.xaccel_channel_name || '-'}</td>
                      <td className="py-2.5 text-center">
                        <span className={clsx(
                          'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                          statusDef.className
                        )}>
                          {t(statusDef.key)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderLinear = () => (
    <div className="card">
      {data.linearChannels.length === 0 ? (
        <p className="text-dark-400 text-sm">{t('admin.noLinear')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('common.name')}</th>
                <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('common.status')}</th>
                <th className="text-center py-2 pr-4 text-dark-400 font-medium">{t('linear.autoRestart')}</th>
                <th className="text-left py-2 text-dark-400 font-medium">{t('linear.xaccelChannel')}</th>
              </tr>
            </thead>
            <tbody>
              {data.linearChannels.map((ch) => (
                <tr key={ch.id} className="border-b border-dark-700 last:border-0">
                  <td className="py-2.5 pr-4 font-medium text-dark-100">{ch.name}</td>
                  <td className="py-2.5 pr-4">
                    <span className={clsx(
                      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                      ch.status === 'running' ? 'bg-emerald-50 text-emerald-600'
                        : ch.status === 'stopped' ? 'bg-gray-100 text-gray-500'
                        : 'bg-amber-50 text-amber-600'
                    )}>
                      {ch.status}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 text-center">
                    {ch.auto_restart_enabled ? (
                      <CheckCircle2 size={16} className="text-emerald-500 inline" />
                    ) : (
                      <span className="text-dark-500">-</span>
                    )}
                  </td>
                  <td className="py-2.5 text-dark-300 font-mono text-xs">{ch.xaccel_channel_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderDazn = () => (
    <div className="card">
      <h3 className="text-base font-semibold text-dark-100 mb-4">{t('admin.daznAccountInfo')}</h3>
      {daznAccount ? (
        <div className="space-y-4">
          {/* Credentials */}
          <div className="border-b border-dark-700 pb-4">
            <h4 className="text-sm font-medium text-dark-300 mb-3">{t('admin.credentials')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-dark-400">Email</label>
                <p className="font-mono text-sm bg-dark-700/50 px-3 py-2 rounded mt-1">{daznAccount.dazn_email || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-dark-400">{t('dazn.password')}</label>
                <p className="font-mono text-sm bg-dark-700/50 px-3 py-2 rounded mt-1">{daznAccount.dazn_password || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-dark-400">{t('dazn.deviceId')}</label>
                <p className="font-mono text-sm bg-dark-700/50 px-3 py-2 rounded mt-1">{daznAccount.dazn_device_id || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-dark-400">{t('dazn.pin')}</label>
                <p className="font-mono text-sm bg-dark-700/50 px-3 py-2 rounded mt-1">{daznAccount.dazn_pin || '-'}</p>
              </div>
            </div>
          </div>

          {/* Region */}
          <div className="border-b border-dark-700 pb-4">
            <h4 className="text-sm font-medium text-dark-300 mb-3">{t('admin.region')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-dark-400">{t('admin.country')}</label>
                <p className="text-sm mt-1">{daznAccount.dazn_country || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-dark-400">{t('settings.language')}</label>
                <p className="text-sm mt-1">{daznAccount.dazn_language || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-dark-400">{t('admin.timezone')}</label>
                <p className="text-sm mt-1">{daznAccount.dazn_timezone || '-'}</p>
              </div>
            </div>
          </div>

          {/* Subscription & Token */}
          <div className="border-b border-dark-700 pb-4">
            <h4 className="text-sm font-medium text-dark-300 mb-3">{t('admin.subscription')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-dark-400">{t('admin.subscription')}</label>
                <p className="text-sm mt-1">{daznAccount.subscription_status || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-dark-400">{t('dazn.tokenStatus')}</label>
                <div className="mt-1">
                  <span className={clsx(
                    'inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium',
                    !daznAccount.tokenStatus.hasToken
                      ? 'bg-gray-100 text-gray-500'
                      : tokenExpired
                        ? 'bg-red-50 text-red-600'
                        : 'bg-emerald-50 text-emerald-600'
                  )}>
                    <Key size={12} />
                    {!daznAccount.tokenStatus.hasToken
                      ? t('admin.tokenMissing')
                      : tokenExpired
                        ? t('admin.tokenExpired')
                        : t('admin.tokenValid')}
                  </span>
                </div>
              </div>
              {daznAccount.dazn_first_name && (
                <div>
                  <label className="text-xs text-dark-400">{t('common.name')}</label>
                  <p className="text-sm mt-1">{daznAccount.dazn_first_name} {daznAccount.dazn_last_name || ''}</p>
                </div>
              )}
            </div>
          </div>

          {/* User Agent */}
          <div>
            <h4 className="text-sm font-medium text-dark-300 mb-3">User Agent</h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-dark-400">{t('dazn.userAgent')}</label>
                <p className="font-mono text-xs bg-dark-700/50 px-3 py-2 rounded mt-1 break-all">{daznAccount.dazn_user_agent || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-dark-400">{t('dazn.xDaznUa')}</label>
                <p className="font-mono text-xs bg-dark-700/50 px-3 py-2 rounded mt-1 break-all">{daznAccount.dazn_x_dazn_ua || '-'}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-dark-400 text-sm">{t('admin.noDaznAccount')}</p>
      )}
    </div>
  );

  const tabContent: Record<DetailTab, () => React.ReactNode> = {
    overview: renderOverview,
    dazn: renderDazn,
    proxies: renderProxies,
    xaccel: renderXaccel,
    sports: renderSports,
    schedule: renderSchedule,
    linear: renderLinear,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-100 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-dark-100">{user.username}</h1>
            <p className="text-dark-400 text-sm">{user.email || t('admin.userDetail')}</p>
          </div>
        </div>
        <span className={clsx(
          'inline-flex items-center px-3 py-1 rounded-md text-sm font-medium',
          user.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
        )}>
          {user.is_active ? t('common.active') : t('common.disabled')}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-dark-700 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={clsx(
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              activeTab === tab.key
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-dark-400 hover:text-dark-200 hover:border-dark-500'
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tabContent[activeTab]()}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-dark-700 last:border-0">
      <span className="text-sm text-dark-400">{label}</span>
      <span className="text-sm text-dark-100 font-medium">
        {typeof value === 'string' ? (value || '-') : value}
      </span>
    </div>
  );
}
